import { useEffect, useRef, useState, useCallback } from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface FeatureVideoProps {
  mp4Src: string;
  poster?: string;
  alt: string;
  isActive: boolean;
  className?: string;
}

export const FeatureVideo = ({
  mp4Src,
  poster,
  alt,
  isActive,
  className = "",
}: FeatureVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const retryingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const maxRetries = 3;

  const doRetry = useCallback(() => {
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetryCount((prev) => {
      const next = prev + 1;
      if (next > maxRetries) {
        setHasError(true);
        setIsLoading(false);
      }
      retryingRef.current = false;
      return next;
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || hasError) return;
    if (isActive && hasLoaded) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive, hasLoaded, hasError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && isActive) {
            video.pause();
          } else if (entry.isIntersecting && isActive && hasLoaded) {
            video.play().catch(() => {});
          }
        });
      },
      { threshold: 0.5 }
    );
    observerRef.current.observe(video);
    return () => {
      observerRef.current?.disconnect();
    };
  }, [isActive, hasLoaded]);

  useEffect(() => {
    if (isActive && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [isActive, hasLoaded]);

  useEffect(() => {
    const video = videoRef.current;
    if (hasLoaded && video) {
      const timer = setTimeout(() => {
        video.load();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [hasLoaded, retryCount]);

  const handleLoadedData = () => {
    setIsLoading(false);
  };

  const handleSourceError = () => {
    doRetry();
  };

  if (hasError) {
    return (
      <div
        className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50 ${className}`}
      >
        <div className="text-center p-8">
          <p className="text-muted-foreground">Video unavailable</p>
          <p className="text-sm text-muted-foreground/60 mt-2">
            Unable to load demo video
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`absolute inset-0 ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
          <Skeleton className="w-full h-full" />
        </div>
      )}

      <video
        ref={videoRef}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
        muted
        loop
        playsInline
        preload={isActive ? "auto" : "metadata"}
        poster={poster}
        onLoadedData={handleLoadedData}
        aria-label={alt}
      >
        {hasLoaded && (
          <source src={mp4Src} type="video/mp4" onError={handleSourceError} />
        )}
        Your browser does not support the video tag.
      </video>
    </div>
  );
};
